import { ConfigProvider, InputNumber, Slider, SliderSingleProps } from 'antd'
import { useEffect, useState } from 'react'

import ResetIcon from '@/assets/icons/reset.svg?react'

export function SliderInput({
  value: propValue,
  defaultValue,
  min,
  max,
  unit,
  step = 1,
  showReset = true,
  showInput = true,
  inputWidth,
  onChange,
}: {
  value?: number
  defaultValue?: number
  min: number
  max: number
  unit?: string
  step?: number
  showReset?: boolean
  showInput?: boolean
  inputWidth?: number
  onChange: (value: number) => void
}) {
  const [internalValue, setInternalValue] = useState(propValue ?? defaultValue ?? min)

  // Sync external value to internal state (controlled mode)
  useEffect(() => {
    if (propValue !== undefined) {
      setInternalValue(propValue)
    }
  }, [propValue])

  const value = propValue !== undefined ? propValue : internalValue

  const marks: SliderSingleProps['marks'] = {
    [min]: (
      <div className="t-xs text-tertiary flex max-w-full mt-1 translate-x-3">{`${min}${unit ? ` ${unit}` : ''}`}</div>
    ),
    [max]: (
      <div className="t-xs text-tertiary flex max-w-full mt-1 text-nowrap -translate-x-1/2">{`${max}${unit ? ` ${unit}` : ''}`}</div>
    ),
  }

  const handleChange = (newValue: number) => {
    const adjustedValue = Math.max(min, Math.min(newValue, max))
    if (propValue === undefined) {
      setInternalValue(adjustedValue)
    }
    onChange(adjustedValue)
  }

  const handleInputChange = (value: number | null) => {
    if (value !== null) {
      handleChange(value)
    }
  }

  const handleReset = () => {
    const resetValue = defaultValue ?? min
    if (propValue === undefined) {
      setInternalValue(resetValue)
    }
    onChange(resetValue)
  }

  return (
    <div className="flex items-center gap-x-4">
      <div className="flex-grow">
        <ConfigProvider
          theme={{
            components: {
              Slider: {
                handleSize: 12,
                trackBg: 'rgb(var(--color-btn-secondary))',
                trackHoverBg: 'rgb(var(--color-btn-secondary-hover))',
              },
            },
          }}
        >
          <Slider
            min={min}
            max={max}
            value={value}
            marks={marks}
            step={step}
            onChange={handleChange}
            className="custom-slider"
          />
        </ConfigProvider>
      </div>
      {showInput && (
        <div
          className="flex items-center h-11 px-2 border border-neutral-200 rounded-lg"
          style={{ width: inputWidth ?? 88 }}
        >
          <InputNumber
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={handleInputChange}
            controls={false}
            className="border-none t-sb pointer-events-none"
          />
          {unit && <div className="t-sb text-tertiary">{unit}</div>}
        </div>
      )}
      {showReset && (
        <ResetIcon width={20} height={20} className="cursor-pointer" onClick={handleReset} />
      )}
    </div>
  )
}
